import { AnyPlaybookType, VideoPlayer } from 'app/types'
import { isPlaylist as checkIsPlaylist } from 'modules'
import { useCallback, useEffect, useMemo } from 'react'
import { useBoolean } from './use-boolean'

const getLocalStorageKey = (playbookId: string) => `scorm-view-${playbookId}`

type Props = {
    playbook: AnyPlaybookType
    videoPlayer: VideoPlayer
}

export const useScorm = ({ playbook, videoPlayer }: Props) => {
    const hasMarkedAsCompleted = useBoolean()
    const setHasMarkedAsCompleted = hasMarkedAsCompleted.setTrue

    const hasViewed = useMemo(() => !!localStorage.getItem(getLocalStorageKey(playbook.id)), [playbook.id])
    const isPlaylist = useMemo(() => checkIsPlaylist(playbook), [playbook])
    const isMarkAsCompletedEnabled = useBoolean(hasViewed || isPlaylist)
    const setIsMarkAsCompletedEnabled = isMarkAsCompletedEnabled.setTrue

    const markAsCompleted = useCallback(() => {
        if (hasMarkedAsCompleted.isTrue) return

        setHasMarkedAsCompleted()
        window.parent.postMessage(
            {
                type: 'guidde-mark-as-completed',
                data: {
                    playbookId: playbook.id,
                },
            },
            '*',
        )
    }, [hasMarkedAsCompleted.isTrue, playbook.id, setHasMarkedAsCompleted])

    useEffect(() => {
        if (isPlaylist) {
            return
        }

        const handleTimeUpdate = () => {
            const duration = videoPlayer.getDuration()
            if (!duration) return

            const percentWatched = videoPlayer.getCurrentTime() / duration
            if (percentWatched <= 0.95) return

            setIsMarkAsCompletedEnabled()
            localStorage.setItem(getLocalStorageKey(playbook.id), 'true')
        }

        videoPlayer.addEventListener(videoPlayer.eventNames.timeUpdate, handleTimeUpdate)

        return () => {
            videoPlayer.removeEventListener(videoPlayer.eventNames.timeUpdate, handleTimeUpdate)
        }
        // Change only on playbook change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setIsMarkAsCompletedEnabled, playbook.id, videoPlayer])

    return {
        isMarkAsCompletedEnabled: isMarkAsCompletedEnabled.isTrue,
        markAsCompleted,
    }
}
